/**
 * Player detail (spec section 59).
 *
 * The header is the strictest part of this screen. It shows five source clocks
 * side by side rather than one "last updated", because the profile refreshes
 * every ~10 minutes while kills and standings move on a ~6 hour board — a
 * single timestamp would be accurate for at most one of the numbers under it
 * (spec section 64).
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePlayer } from '../../api/queries';
import { PageHeader, Tabs, Value } from '../../components/ui/primitives';
import { QueryBoundary } from '../../components/ui/states';
import { PlayerFreshnessStrip } from '../../components/freshness/Freshness';
import { Qualified, QualitySummary } from '../../components/quality/Qualified';
import { formatCompact, formatTc } from '../../lib/format';
import { useT } from '../../i18n';
import { GrowthTab } from './tabs/GrowthTab';
import { HeroesTab } from './tabs/HeroesTab';
import { CapabilitiesTab } from './tabs/CapabilitiesTab';
import { PlayerBearTab } from './tabs/PlayerBearTab';
import { EventHistoryTab } from './tabs/EventHistoryTab';

const TAB_IDS = ['growth', 'heroes', 'capabilities', 'bear', 'events'] as const;

type TabId = (typeof TAB_IDS)[number];

export function PlayerDetailPage() {
  const { playerId } = useParams<{ playerId: string }>();
  const player = usePlayer(playerId);
  const [tab, setTab] = useState<TabId>('growth');
  const t = useT();

  const tabs = TAB_IDS.map((id) => ({ id, label: t.player.tabs[id] }));

  return (
    <QueryBoundary query={player} loadingLabel={t.player.loading}>
      {(data) => (
        <>
          <PageHeader
            title={<Value absent={t.common.unnamedPlayer}>{data.player.name}</Value>}
            subtitle={
              <div className="stack" style={{ gap: 6 }}>
                <div className="row" style={{ gap: 14 }}>
                  <Qualified
                    field={t.common.townCentreLevel}
                    quality={data.player.quality['townHallLevel']}
                    value={formatTc(data.player.townHallLevel)}
                  />
                  <span>
                    <span className="faint">{t.common.power} </span>
                    <Qualified
                      field={t.common.power}
                      quality={data.player.quality['power']}
                      value={formatCompact(data.player.power)}
                    />
                  </span>
                  <span>
                    <span className="faint">{t.common.vip} </span>
                    <Qualified
                      field={t.common.vip}
                      quality={data.player.quality['vip']}
                      value={data.player.vip}
                    />
                  </span>
                  <span>
                    <span className="faint">{t.common.alliance} </span>
                    <Value absent={t.common.noAlliance}>
                      {data.player.allianceAbbr ?? data.player.allianceName}
                    </Value>
                  </span>
                  <QualitySummary quality={data.player.quality} />
                </div>
                <PlayerFreshnessStrip freshness={data.player.freshness} />
              </div>
            }
            actions={
              data.player.avatarUrl ? (
                <img
                  src={data.player.avatarUrl}
                  alt=""
                  width={48}
                  height={48}
                  style={{ borderRadius: 8, border: '1px solid var(--border)' }}
                />
              ) : null
            }
          />

          <Tabs tabs={tabs} active={tab} onChange={setTab} />

          {tab === 'growth' ? <GrowthTab playerId={data.player.id} growth={data.growth} /> : null}
          {tab === 'heroes' ? <HeroesTab playerId={data.player.id} /> : null}
          {tab === 'capabilities' ? <CapabilitiesTab playerId={data.player.id} /> : null}
          {tab === 'bear' ? <PlayerBearTab playerId={data.player.id} /> : null}
          {tab === 'events' ? <EventHistoryTab /> : null}
        </>
      )}
    </QueryBoundary>
  );
}
